/**
 * Client portal API: profile update, logo/settings, geofencing, vehicles, drivers.
 * Uses contractor_token via getAuthHeaders(). Endpoints are real or temporary until backend adds client-scoped routes.
 */

import { buildApiUrlWithQuery, getAuthHeaders, getAuthToken, getRegionForApi, VALT_API_BASE, valtFetch } from '@/utils/api'
import { withNormalizedBikeIdentityFields, readBikeCanSoc1, readBikeCanSoc2, readBikeRemainingRangeKm } from '@/utils/bikeFields'
import {
  isBikeAssetType,
  normalizeVehicleTypeSlug,
  readClientAssetType,
} from '@/utils/clientAssetTypes'
import { contractorAuthApi, type Contractor, type UpdateContractorPayload } from '@/services/contractorService'
import { SINGLE_SCHEMA_ENABLED } from '@/config/singleSchema'

const API_BASE = VALT_API_BASE

/** Coalesce parallel identical GETs within the same tick (e.g. listClientVehicles + listClientBikes). */
function dedupeInflight<T>(store: { current: Promise<T> | null }, factory: () => Promise<T>): Promise<T> {
  if (store.current) return store.current
  const promise = factory().finally(() => {
    store.current = null
  })
  store.current = promise
  return promise
}

const clientAssetsInflight: { current: Promise<ClientVehicle[]> | null } = { current: null }
const clientAssetsDetailsInflight: { current: Promise<ClientVehicle[]> | null } = { current: null }
const historyInflight = new Map<string, Promise<ClientVehicleHistoryResult>>()
const bikeHistoryInflight = new Map<string, Promise<ClientBikeHistoryResult>>()

export class ClientPortalApiError extends Error {
  status?: number
  response?: unknown

  constructor(message: string, options?: { status?: number; response?: unknown }) {
    super(message)
    this.name = 'ClientPortalApiError'
    this.status = options?.status
    this.response = options?.response
  }
}

function getClientIdFromStorage(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem('contractor')
    if (raw) {
      const parsed = JSON.parse(raw) as any
      const candidateIds = [parsed?.id, parsed?.client_id, parsed?.contractor_id, parsed?.user_context?.contractor_id]
      for (const id of candidateIds) {
        if (id !== null && id !== undefined && id !== '') return String(id)
      }
    }
  } catch {
    // Ignore malformed localStorage and continue to token fallback.
  }

  try {
    const token = getAuthToken()
    if (!token) return null
    const parts = token.split('.')
    if (parts.length !== 3 || !parts[1]) return null
    const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(payloadJson) as Record<string, unknown>
    const candidateIds = [
      payload.contractor_id,
      payload.client_id,
      payload.tenant_id,
      (payload.user_context as any)?.contractor_id,
      payload.id,
    ]
    for (const id of candidateIds) {
      if (id !== null && id !== undefined && id !== '') return String(id)
    }
  } catch {
    return null
  }
  return null
}

export interface ClientVehicle {
  /** valt_vehicles.id — list key, driver assignment, etc. */
  id: string
  /** Fleet asset id — join key for driver assignments (same as id when API is consistent). */
  valt_vehicle_id?: number
  name: string
  number_plate?: string
  status?: string
  lat?: number
  lng?: number
  /** Heading/direction in degrees 0–360 from API. */
  heading?: number
  /** Speed in km/h from API. */
  speed?: number
  /** Battery state of charge 0–100 when API provides it. */
  soc?: number
  /** Bike dual-pack CAN SOC 1 (0–100) when telematics provides it. */
  canSoc1?: number
  /** Bike dual-pack CAN SOC 2 (0–100) when telematics provides it. */
  canSoc2?: number
  /** Estimated range upper bound in km when available from CAN/telematics payload. */
  range_km_max?: number
  /** Estimated range lower bound in km when available from CAN/telematics payload. */
  range_km_min?: number
  last_update?: string
  /** Go Metro device id — required for GET /client/vehicles/{id}/history (not valt_vehicle id). */
  go_metro_device_id?: string
  go_metro_vehicle_id?: number
  primary_status?: string
  sub_status?: string
  last_ignition_status?: boolean
  /** Unified asset kind — `vehicle`, `bike`, or future types from API. */
  type?: string
  vehicle_type?: string
  /** Registry id when asset is a bike in the unified model. */
  go_metro_bike_id?: number
  [key: string]: unknown
}

/** Row from GET /client/drivers — use `id` (client_drivers.id) for DELETE; `driver_id` for POST link. */
export interface ClientDriver {
  id: string | number
  /** Uber / valt_os driver UUID — required for POST link/create follow-ups */
  driver_id?: string
  /** Same as `id` when API exposes client_drivers primary key explicitly */
  client_driver_id?: string | number
  name: string
  phone?: string
  email?: string
  status?: string
  /** Assigned valt vehicle id when linked */
  valt_vehicle_id?: number
  /** Assigned go_metro_bikes registry id when linked */
  go_metro_bike_id?: number
  /** Alias for go_metro_bike_id from some API responses */
  bike_id?: number
  /** Assigned bike device id / IMEI when linked (legacy) */
  bike_device_id?: string
  [key: string]: unknown
}

/** Unified valt_vehicle primary key for any client asset (car, bike, etc.). */
export function readClientAssetValtVehicleId(asset: ClientVehicle | null | undefined): number | null {
  if (!asset) return null
  const n = Number(asset.valt_vehicle_id ?? asset.id)
  return Number.isFinite(n) ? n : null
}

/** Find the driver assigned to an asset via driver.valt_vehicle_id. */
export function findDriverAssignedToClientAsset(
  asset: ClientVehicle,
  drivers: ClientDriver[],
): ClientDriver | undefined {
  const vvid = readClientAssetValtVehicleId(asset)
  if (vvid == null) return undefined
  return drivers.find((d) => d.valt_vehicle_id != null && Number(d.valt_vehicle_id) === vvid)
}

/** POST /client/drivers — mode defaults to link on the server; we send it explicitly. */
export type PostClientDriverLinkByDriverId = {
  mode?: 'link'
  driver_id: string
  valt_vehicle_id?: number
  bike_id?: number
  go_metro_bike_id?: number
  bike_device_id?: string
}

export type PostClientDriverLinkByPhone = {
  mode?: 'link'
  phone_number: string
  valt_vehicle_id?: number
  bike_id?: number
  go_metro_bike_id?: number
  bike_device_id?: string
}

export type PostClientDriverCreate = {
  mode: 'create'
  first_name: string
  last_name: string
  phone_number: string
  email?: string
  valt_vehicle_id?: number
  bike_id?: number
  go_metro_bike_id?: number
  bike_device_id?: string
}

export type PostClientDriverPayload =
  | PostClientDriverLinkByDriverId
  | PostClientDriverLinkByPhone
  | PostClientDriverCreate

export interface PostClientDriverResult {
  created_new_uber_driver?: boolean
  success?: boolean
  message?: string
  data?: unknown
  [key: string]: unknown
}

export interface GeofenceSetting {
  id?: string | number
  name: string
  lat: number
  lng: number
  radius_km: number
  enabled: boolean
}

export interface ClientVehicleAssignmentResponse {
  success?: boolean
  message?: string
  data?: unknown
  [key: string]: unknown
}

/** Update client profile (PATCH client/profile or similar). */
export async function updateClientProfile(
  payload: Partial<UpdateContractorPayload>,
): Promise<{ contractor: Contractor }> {
  const region = getRegionForApi()
  const res = await valtFetch(`${API_BASE}/client/profile?region=${region}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || data.message || 'Failed to update profile')
  if (data.client && !data.contractor) return { contractor: data.client as Contractor }
  return data
}

/** Change password for the logged-in client portal account. */
export async function changeClientPassword(payload: {
  current_password: string
  new_password: string
  new_password_confirmation: string
}): Promise<void> {
  await contractorAuthApi.changePassword(payload)
}

/** Upload logo/image for client (temporary: POST client/logo or placeholder). */
export async function uploadClientLogo(file: File): Promise<{ url: string }> {
  const region = getRegionForApi()
  const form = new FormData()
  form.append('logo', file)
  const res = await valtFetch(`${API_BASE}/client/logo?region=${region}`, {
    method: 'POST',
    headers: { Accept: 'application/json', ...getAuthHeaders({ contentType: false }) },
    body: form,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.message || data.error || 'Logo upload failed')
  }
  const data = await res.json()
  return { url: data.url || data.logo_url || URL.createObjectURL(file) }
}

/** Get geofencing settings (temporary endpoint or mock). */
export async function getClientGeofencing(): Promise<GeofenceSetting[]> {
  try {
    const region = getRegionForApi()
    const res = await valtFetch(`${API_BASE}/client/geofencing?region=${region}`, { headers: getAuthHeaders() })
    if (!res.ok) return []
    const data = await res.json()
    return Array.isArray(data) ? data : data.data || data.geofences || []
  } catch {
    return []
  }
}

/** Update geofencing (temporary endpoint or mock). */
export async function updateClientGeofencing(geofences: GeofenceSetting[]): Promise<GeofenceSetting[]> {
  try {
    const region = getRegionForApi()
    const res = await valtFetch(`${API_BASE}/client/geofencing?region=${region}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ geofences }),
    })
    if (!res.ok) throw new Error('Failed to update geofencing')
    const data = await res.json()
    return Array.isArray(data) ? data : data.data || data.geofences || geofences
  } catch {
    return geofences
  }
}

function readNumber(obj: any, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = obj?.[k]
    if (typeof v === 'number' && Number.isFinite(v)) return v
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v)
      if (Number.isFinite(n)) return n
    }
  }
  return undefined
}

/** Lat/lng from flat fields or nested position/location (API shapes vary). */
function readVehicleLatLng(vv: any, row?: any): { lat?: number; lng?: number } {
  let lat = readNumber(vv, 'lat', 'latitude')
  let lng = readNumber(vv, 'lng', 'longitude', 'lon', 'long')
  if (lat != null && lng != null) return { lat, lng }
  const pos = vv?.position ?? vv?.location ?? vv?.gps ?? row?.position ?? row?.location
  if (pos && typeof pos === 'object') {
    const o = pos as Record<string, unknown>
    lat = readNumber(o, 'lat', 'latitude', 'y')
    lng = readNumber(o, 'lng', 'longitude', 'lon', 'long', 'x')
    if (lat != null && lng != null) return { lat, lng }
  }
  return {}
}

function readVehicleSpeedKmh(vv: any): number | undefined {
  return readNumber(
    vv,
    'speed',
    'groundSpeed',
    'ground_speed',
    'velocity_kmh',
    'velocityKmh',
    'speed_kmh',
    'speedKmh',
    'canVehicleSpeed',
    'can_vehicle_speed',
  )
}

function readVehicleSoc(vv: any): number | undefined {
  const n = readNumber(
    vv,
    'soc',
    'battery_soc',
    'batterySoc',
    'state_of_charge',
    'stateOfCharge',
    'battery_percent',
    'batteryPercent',
    'canStateOfCharge',
    'can_state_of_charge',
  )
  if (n == null) return undefined
  return Math.max(0, Math.min(100, n))
}

function readVehicleRangeMax(vv: any, row?: any): number | undefined {
  return (
    readNumber(
      vv,
      'canRemainingRangeMaximum',
      'can_remaining_range_maximum',
      'remainingRangeMax',
      'range_km_max',
      'rangeKmMax',
      'max_range_km',
      'range_max_km',
      'estimated_range_km',
      'range_max',
      'maxRange',
    ) ??
    readNumber(
      row,
      'canRemainingRangeMaximum',
      'can_remaining_range_maximum',
      'remainingRangeMax',
      'range_km_max',
      'rangeKmMax',
      'max_range_km',
      'range_max_km',
      'estimated_range_km',
      'range_max',
      'maxRange',
    )
  )
}

function readVehicleRangeMin(vv: any, row?: any): number | undefined {
  return (
    readNumber(
      vv,
      'canRemainingRangeMinimum',
      'can_remaining_range_minimum',
      'remainingRangeMin',
      'range_km_min',
      'rangeKmMin',
      'min_range_km',
      'range_min_km',
      'minimum_range_km',
      'range_min',
      'minRange',
    ) ??
    readNumber(
      row,
      'canRemainingRangeMinimum',
      'can_remaining_range_minimum',
      'remainingRangeMin',
      'range_km_min',
      'rangeKmMin',
      'min_range_km',
      'range_min_km',
      'minimum_range_km',
      'range_min',
      'minRange',
    )
  )
}

function readHeading(vv: any, row?: any): number | undefined {
  const num = (v: unknown): number | undefined => {
    if (typeof v === 'number' && Number.isFinite(v)) return v
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v)
      return Number.isFinite(n) ? n : undefined
    }
    return undefined
  }
  /** Log / vehicle payloads use `direction` (0–360°, clockwise from north); prefer it over `heading`. */
  const h =
    num(vv?.direction) ??
    num(vv?.positionDirection) ??
    num((vv as any)?.positionDirection) ??
    num(vv?.heading) ??
    num(vv?.course) ??
    num(vv?.bearing) ??
    num(vv?.rotation) ??
    num(row?.position?.direction) ??
    num((row as any)?.['position.direction'])
  if (h == null) return undefined
  return ((h % 360) + 360) % 360
}

function readIgnition(vv: any, row?: any): boolean | undefined {
  const vals = [
    vv?.last_ignition_status,
    vv?.ignition,
    vv?.engine_on,
    vv?.engineOn,
    vv?.engine_status,
    row?.last_ignition_status,
    row?.ignition,
    row?.engine_on,
    row?.engineOn,
    row?.engine_status,
    row?.go_metro_vehicle?.last_ignition_status,
    row?.go_metro_vehicle?.ignition,
    row?.go_metro_vehicle?.engine_on,
    row?.go_metro_vehicle?.engine_status,
  ]
  for (const v of vals) {
    if (typeof v === 'boolean') return v
    if (v === 1 || v === '1') return true
    if (v === 0 || v === '0') return false
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase()
      if (s === 'on' || s === 'true') return true
      if (s === 'off' || s === 'false') return false
    }
  }
  return undefined
}

function normalizeClientVehicleRow(row: any): ClientVehicle | null {
  const vv = row?.valt_vehicle && typeof row.valt_vehicle === 'object' ? row.valt_vehicle : row
  if (!vv || typeof vv !== 'object') return null
  const gm = row?.go_metro_vehicle && typeof row.go_metro_vehicle === 'object' ? row.go_metro_vehicle : null
  const goMetroDeviceId =
    gm?.device_id != null && String(gm.device_id).trim() !== ''
      ? String(gm.device_id)
      : row?.device_id != null && String(row.device_id).trim() !== ''
        ? String(row.device_id)
        : undefined
  const goMetroVehicleId =
    gm?.id != null && Number.isFinite(Number(gm.id))
      ? Number(gm.id)
      : row?.go_metro_vehicle_id != null && Number.isFinite(Number(row.go_metro_vehicle_id))
        ? Number(row.go_metro_vehicle_id)
        : undefined
  const heading = readHeading(vv, row)
  const { lat: latLL, lng: lngLL } = readVehicleLatLng(vv, row)
  const speedKmh = readVehicleSpeedKmh(vv)
  const soc = readVehicleSoc(vv)
  const canSoc1 = readBikeCanSoc1(vv) ?? readBikeCanSoc1(row)
  const canSoc2 = readBikeCanSoc2(vv) ?? readBikeCanSoc2(row)
  const rangeKmMax = readVehicleRangeMax(vv, row) ?? readBikeRemainingRangeKm(vv) ?? readBikeRemainingRangeKm(row)
  const rangeKmMin = readVehicleRangeMin(vv, row)
  const ignition = readIgnition(vv, row)
  const primaryStatus = (vv.primary_status ?? row?.primary_status ?? row?.status) as string | undefined
  const subStatus = (vv.sub_status ?? row?.sub_status) as string | undefined
  const assetType = readClientAssetType(row)
  const goMetroBikeIdRaw = row?.go_metro_bike_id ?? (assetType === 'bike' ? vv.id ?? row?.id : undefined)
  const goMetroBikeId =
    goMetroBikeIdRaw != null && goMetroBikeIdRaw !== '' && Number.isFinite(Number(goMetroBikeIdRaw))
      ? Number(goMetroBikeIdRaw)
      : undefined
  const valtVehicleId =
    readNumber(row, 'valt_vehicle_id') ??
    readNumber(vv, 'id', 'valt_vehicle_id', 'vehicle_id') ??
    readNumber(row, 'id')
  return {
    uber_vehicle: row?.uber_vehicle ?? null,
    valt_vehicle: row?.valt_vehicle ?? null,
    go_metro_vehicle: row?.go_metro_vehicle ?? null,
    ...vv,
    id: String(valtVehicleId ?? vv.id ?? row?.valt_vehicle_id ?? vv.vehicle_id ?? row?.id ?? ''),
    valt_vehicle_id: valtVehicleId,
    name: vv.name ?? row?.name ?? gm?.name ?? vv.number_plate ?? vv.license_plate ?? 'Vehicle',
    number_plate: vv.number_plate ?? row?.number_plate ?? vv.license_plate ?? gm?.license_plate,
    status: (primaryStatus ?? subStatus ?? vv.status ?? gm?.status) as string | undefined,
    primary_status: primaryStatus,
    sub_status: subStatus,
    lat: latLL ?? readNumber(vv, 'lat', 'latitude') ?? readNumber(row, 'lat', 'latitude'),
    lng:
      lngLL ?? readNumber(vv, 'lng', 'longitude', 'lon', 'long') ?? readNumber(row, 'lng', 'longitude', 'lon', 'long'),
    heading,
    speed: speedKmh,
    soc,
    canSoc1,
    canSoc2,
    range_km_max: rangeKmMax,
    range_km_min: rangeKmMin,
    last_update: vv.last_update ?? vv.lastUpdate ?? row?.last_update,
    go_metro_device_id: goMetroDeviceId,
    go_metro_vehicle_id: goMetroVehicleId,
    go_metro_bike_id: goMetroBikeId,
    type: assetType,
    vehicle_type: String(row?.vehicle_type ?? row?.type ?? assetType),
    last_ignition_status: ignition,
  } as ClientVehicle
}

function parseClientAssetListPayload(data: unknown): any[] {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  const d = data as Record<string, unknown>
  const topLevel = [d.vehicles, d.data, d.assets, d.items].find(Array.isArray)
  if (Array.isArray(topLevel)) return topLevel

  // Nested envelope: { data: { vehicles: [...] } }
  if (d.data && typeof d.data === 'object' && !Array.isArray(d.data)) {
    const nested = d.data as Record<string, unknown>
    const nestedList = [nested.vehicles, nested.assets, nested.items, nested.data].find(Array.isArray)
    if (Array.isArray(nestedList)) return nestedList
  }

  if (d.valt_vehicle) return [d]

  // Assignment ids only (same fallback Console admin list uses)
  const client = d.client as Record<string, unknown> | undefined
  const md = (client?.metadata ?? d.metadata) as Record<string, unknown> | undefined
  const ids = md?.assigned_valt_vehicle_ids
  if (Array.isArray(ids) && ids.length > 0) {
    return ids.map((id) => ({ id, valt_vehicle: { id } }))
  }

  return []
}

async function tryFetchAssetList(url: string): Promise<any[]> {
  try {
    const res = await valtFetch(url, { headers: getAuthHeaders() })
    if (!res.ok) return []
    const data = await res.json().catch(() => null)
    return parseClientAssetListPayload(data)
  } catch {
    return []
  }
}

/**
 * Raw rows from unified client asset endpoints.
 * Prefer GET /clients/{id}/vehicles (authoritative fleet list), then GET /client/vehicles.
 */
async function fetchClientAssetRows(options?: { details?: boolean }): Promise<any[]> {
  const region = encodeURIComponent(getRegionForApi())
  const clientId = getClientIdFromStorage()
  const perPage = 400
  const suffix = options?.details ? `vehicles-details?region=${region}&per_page=${perPage}` : `vehicles?region=${region}`

  const urls: string[] = []
  if (clientId) {
    urls.push(`${API_BASE}/clients/${encodeURIComponent(clientId)}/${suffix}`)
  }
  urls.push(`${API_BASE}/client/${suffix}`)

  for (const url of urls) {
    const rows = await tryFetchAssetList(url)
    if (rows.length > 0) return rows
  }

  if (!options?.details) {
    return fetchClientAssetRows({ details: true })
  }

  return []
}

function normalizeClientAssetList(rows: any[]): ClientVehicle[] {
  return rows
    .map((row: any) => normalizeClientVehicleRow(row))
    .filter((v: ClientVehicle | null): v is ClientVehicle => !!v && !!String(v.id))
}

/** Row from GET /vehicle-types — catalog of asset kinds for filter pills. */
export interface VehicleType {
  id?: string | number
  slug: string
  name: string
  label?: string
  plural_label?: string
  icon?: string
  [key: string]: unknown
}

function normalizeVehicleTypeRow(row: unknown): VehicleType | null {
  if (!row || typeof row !== 'object') return null
  const r = row as Record<string, unknown>
  const slugRaw = r.slug ?? r.code ?? r.key ?? r.vehicle_type ?? r.type
  if (slugRaw == null || String(slugRaw).trim() === '') return null
  const slug = normalizeVehicleTypeSlug(slugRaw)
  const name = String(r.name ?? r.label ?? r.title ?? slug).trim()
  return {
    ...r,
    id: r.id as string | number | undefined,
    slug,
    name,
    label: r.label != null ? String(r.label) : undefined,
    plural_label: r.plural_label != null ? String(r.plural_label) : undefined,
    icon: r.icon != null ? String(r.icon) : undefined,
  }
}

function dedupeVehicleTypes(rows: VehicleType[]): VehicleType[] {
  const seen = new Set<string>()
  const out: VehicleType[] = []
  for (const row of rows) {
    if (!row?.slug || seen.has(row.slug)) continue
    seen.add(row.slug)
    out.push(row)
  }
  return out
}

function parseVehicleTypeList(data: unknown): VehicleType[] {
  if (Array.isArray(data)) {
    return dedupeVehicleTypes(
      data.map((row) => normalizeVehicleTypeRow(row)).filter((row): row is VehicleType => !!row),
    )
  }
  if (!data || typeof data !== 'object') return []

  const d = data as Record<string, unknown>
  const list = [d.data, d.vehicle_types, d.types, d.items].find(Array.isArray)
  if (Array.isArray(list)) {
    return dedupeVehicleTypes(
      list.map((row) => normalizeVehicleTypeRow(row)).filter((row): row is VehicleType => !!row),
    )
  }

  const nested = d.data
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return dedupeVehicleTypes(
      Object.entries(nested as Record<string, unknown>)
        .map(([key, value]) => {
          const payload =
            value && typeof value === 'object' && !Array.isArray(value)
              ? { slug: key, ...(value as Record<string, unknown>) }
              : { slug: key, name: value }
          return normalizeVehicleTypeRow(payload)
        })
        .filter((row): row is VehicleType => !!row),
    )
  }

  return []
}

/** GET /vehicle-types/client — asset types enabled for the authenticated client tenant. */
export async function listClientVehicleTypes(): Promise<VehicleType[]> {
  try {
    const region = getRegionForApi()
    const res = await valtFetch(`${API_BASE}/vehicle-types/client?region=${encodeURIComponent(region)}`, {
      headers: getAuthHeaders(),
    })
    if (!res.ok) return []
    const data = await res.json()
    return parseVehicleTypeList(data)
  } catch {
    return []
  }
}

/** GET /vehicle-types — global catalog (admin); prefer {@link listClientVehicleTypes} in portal. */
export async function listVehicleTypes(): Promise<VehicleType[]> {
  try {
    const region = getRegionForApi()
    const res = await valtFetch(`${API_BASE}/vehicle-types?region=${encodeURIComponent(region)}`, {
      headers: getAuthHeaders(),
    })
    if (!res.ok) return []
    const data = await res.json()
    return parseVehicleTypeList(data)
  } catch {
    return []
  }
}

/** Merge API catalog with types present on assigned fleet rows. Always keep `vehicle`. */
export function mergeVehicleTypesWithFleet(
  catalog: VehicleType[],
  assets: ClientVehicle[],
): VehicleType[] {
  const bySlug = new Map<string, VehicleType>()
  for (const type of catalog) {
    const slug = normalizeVehicleTypeSlug(type.slug)
    // Collapse car → vehicle so the filter shows one "vehicles" option.
    const key = slug === 'car' ? 'vehicle' : slug
    if (key === 'vehicle') {
      bySlug.set('vehicle', {
        ...type,
        slug: 'vehicle',
        name: 'vehicles',
        label: 'vehicles',
        plural_label: type.plural_label || 'vehicles',
      })
      continue
    }
    if (!bySlug.has(key)) bySlug.set(key, { ...type, slug: key })
  }
  for (const asset of assets) {
    const slug = readClientAssetType(asset)
    const key = slug === 'car' ? 'vehicle' : slug
    if (!bySlug.has(key)) {
      bySlug.set(key, {
        slug: key,
        name: key === 'vehicle' ? 'vehicles' : key,
        label: key === 'vehicle' ? 'vehicles' : key,
      })
    }
  }
  if (!bySlug.has('vehicle')) {
    bySlug.set('vehicle', { slug: 'vehicle', name: 'vehicles', label: 'vehicles' })
  }
  return [...bySlug.values()].sort((a, b) => {
    if (a.slug === 'vehicle') return -1
    if (b.slug === 'vehicle') return 1
    return a.name.localeCompare(b.name)
  })
}

/** All client assets from the unified vehicles endpoint (vehicles, bikes, and future types). */
export async function listClientAssets(): Promise<ClientVehicle[]> {
  return dedupeInflight(clientAssetsInflight, async () => {
    try {
      return normalizeClientAssetList(await fetchClientAssetRows())
    } catch {
      return []
    }
  })
}

/** Telemetry-rich unified asset list. */
export async function listClientAssetsDetails(): Promise<ClientVehicle[]> {
  return dedupeInflight(clientAssetsDetailsInflight, async () => {
    try {
      return normalizeClientAssetList(await fetchClientAssetRows({ details: true }))
    } catch {
      return listClientAssets()
    }
  })
}

function motorVehiclesFromAssets(assets: ClientVehicle[]): ClientVehicle[] {
  return assets.filter((v) => !isBikeAssetType(readClientAssetType(v)))
}

export type ClientFleetLists = {
  assets: ClientVehicle[]
  vehicles: ClientVehicle[]
  bikes: ClientBike[]
}

/** One GET /client/vehicles — split into motor vehicles and bikes for pages that need both. */
export async function listClientFleet(): Promise<ClientFleetLists> {
  const assets = await listClientAssets()
  return {
    assets,
    vehicles: motorVehiclesFromAssets(assets),
    bikes: unifiedClientBikesFromAssets(assets),
  }
}

/** Live telemetry row from GET /client/vehicles/updates (keyed by deviceId). Same shape as Tracker GET /vehicles/updates. */
export type VehicleTelemetryUpdate = {
  deviceId: string
  timestamp?: number
  latitude?: number | null
  longitude?: number | null
  ignitionStatus?: boolean | null
  speed?: number | null
  supplyVoltage?: number | null
  soc?: number | null
  voltage?: number | null
  current?: number | null
  [key: string]: unknown
}

/** Index GET /client/vehicles/updates `data[]` by deviceId. */
export function buildVehicleTelemetryIndex(updates: unknown): Record<string, VehicleTelemetryUpdate> {
  const index: Record<string, VehicleTelemetryUpdate> = {}
  if (!Array.isArray(updates)) return index
  for (const row of updates) {
    if (!row || typeof row !== 'object') continue
    const id = String((row as VehicleTelemetryUpdate).deviceId ?? '').trim()
    if (id) index[id] = row as VehicleTelemetryUpdate
  }
  return index
}

/**
 * Bulk live telemetry for the client's fleet (same shape as Tracker GET /vehicles/updates).
 * Cache-bust with `_t`. On auth/network failure returns [] so catalog still renders.
 */
export async function fetchVehicleUpdates(): Promise<VehicleTelemetryUpdate[]> {
  try {
    const region = getRegionForApi()
    const qs = new URLSearchParams({
      region,
      _t: String(Date.now()),
    })
    const res = await valtFetch(`${API_BASE}/client/vehicles/updates?${qs}`, {
      headers: { ...getAuthHeaders(), 'X-Region': region, 'Cache-Control': 'no-cache' },
    })
    if (!res.ok) return []
    const json = (await res.json().catch(() => ({}))) as { data?: unknown }
    const rows = Array.isArray(json?.data) ? json.data : []
    return rows.filter((r): r is VehicleTelemetryUpdate => !!r && typeof r === 'object' && !!(r as any).deviceId)
  } catch {
    return []
  }
}

function resolveAssetDeviceId(asset: ClientVehicle): string | null {
  const nested = asset.go_metro_vehicle as Record<string, unknown> | null | undefined
  const candidates = [asset.go_metro_device_id, nested?.device_id, asset.device_id, (asset as Record<string, unknown>).deviceId]
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() !== '') return c.trim()
    if (typeof c === 'number' && Number.isFinite(c)) return String(c)
  }
  return null
}

/** True when assigned asset has a telematics / Go Metro device id. */
export function clientAssetHasTelemetryDevice(asset: ClientVehicle | null | undefined): boolean {
  if (!asset) return false
  return resolveAssetDeviceId(asset) != null
}

/** Join catalog rows with /client/vehicles/updates telemetry. */
export function joinAssetsWithTelemetryUpdates(
  assets: ClientVehicle[],
  updates: VehicleTelemetryUpdate[] | Record<string, VehicleTelemetryUpdate>,
): ClientVehicle[] {
  const index = Array.isArray(updates) ? buildVehicleTelemetryIndex(updates) : updates
  if (!Object.keys(index).length) return assets

  return assets.map((asset) => {
    const deviceId = resolveAssetDeviceId(asset)
    if (!deviceId) return asset
    const t = index[deviceId]
    if (!t) return asset

    const lat = typeof t.latitude === 'number' && Number.isFinite(t.latitude) ? t.latitude : asset.lat
    const lng = typeof t.longitude === 'number' && Number.isFinite(t.longitude) ? t.longitude : asset.lng
    const speed = typeof t.speed === 'number' && Number.isFinite(t.speed) ? t.speed : asset.speed
    const soc = typeof t.soc === 'number' && Number.isFinite(t.soc) ? t.soc : asset.soc
    const ign =
      typeof t.ignitionStatus === 'boolean' ? t.ignitionStatus : asset.last_ignition_status
    const canSoc1 = readBikeCanSoc1(t) ?? asset.canSoc1
    const canSoc2 = readBikeCanSoc2(t) ?? asset.canSoc2
    const bikeRange = readBikeRemainingRangeKm(t)
    const rangeKmMax = bikeRange ?? asset.range_km_max
    const supplyVoltage =
      readNumber(t, 'supplyVoltage', 'supply_voltage') ?? readNumber(asset, 'supplyVoltage', 'supply_voltage')
    const batteryLevel =
      readNumber(t, 'batteryLevel', 'battery_level', 'batteryPercent', 'battery_percent') ??
      readNumber(asset, 'batteryLevel', 'battery_level', 'batteryPercent', 'battery_percent')

    return {
      ...asset,
      lat,
      lng,
      speed,
      soc,
      canSoc1,
      canSoc2,
      range_km_max: rangeKmMax,
      last_ignition_status: ign,
      go_metro_device_id: asset.go_metro_device_id ?? deviceId,
      ...(supplyVoltage != null ? { supplyVoltage } : {}),
      ...(batteryLevel != null ? { batteryLevel } : {}),
    }
  })
}

/**
 * Catalog + live telemetry join for tracker map (Tracker App.tsx principle).
 * Telemetry failure degrades to catalog-only rather than blanking the fleet.
 */
export async function listClientFleetLive(): Promise<ClientFleetLists> {
  const [fleet, updates] = await Promise.all([
    listClientFleet(),
    fetchVehicleUpdates(),
  ])
  const assets = joinAssetsWithTelemetryUpdates(fleet.assets, updates)
  return {
    assets,
    vehicles: motorVehiclesFromAssets(assets),
    bikes: unifiedClientBikesFromAssets(assets),
  }
}

/** List motor vehicles for the client (unified /client/vehicles, bikes filtered out). */
export async function listClientVehicles(): Promise<ClientVehicle[]> {
  try {
    return motorVehiclesFromAssets(await listClientAssets())
  } catch {
    return []
  }
}

/** Full payload for live tracker and detailed vehicle telemetry shape. */
export async function listClientVehiclesDetails(): Promise<ClientVehicle[]> {
  try {
    const all = await listClientAssetsDetails()
    return motorVehiclesFromAssets(all)
  } catch {
    return []
  }
}

export interface ClientVehicleHistoryResult {
  items: Record<string, unknown>[]
  range?: { from?: string; to?: string }
}

/**
 * Latest tracking points for client portal live tracker (Go Metro device_id in path, not valt_vehicle.id).
 * @example GET .../client/vehicles/862272081743892/history?region=johannesburg&preset=today&perPage=1
 */
export async function getClientVehicleHistory(params: {
  /** Go Metro device id from go_metro_vehicle.device_id */
  deviceId: string | number
  preset?: string
  from?: string
  to?: string
  perPage?: number
}): Promise<ClientVehicleHistoryResult> {
  const preset = params.preset ?? 'today'
  const perPage = params.perPage ?? 50
  const id = String(params.deviceId)
  const cacheKey = `${id}:${preset}:${perPage}:${params.from ?? ''}:${params.to ?? ''}`
  const cached = historyInflight.get(cacheKey)
  if (cached) return cached

  const promise = fetchClientVehicleHistory(params).finally(() => {
    historyInflight.delete(cacheKey)
  })
  historyInflight.set(cacheKey, promise)
  return promise
}

async function fetchClientVehicleHistory(params: {
  deviceId: string | number
  preset?: string
  from?: string
  to?: string
  perPage?: number
}): Promise<ClientVehicleHistoryResult> {
  const region = getRegionForApi()
  const preset = params.preset ?? 'today'
  const perPage = params.perPage ?? 50
  const id = encodeURIComponent(String(params.deviceId))
  const query = new URLSearchParams({
    region,
    preset,
    perPage: String(perPage),
  })
  if (params.from) query.set('from', params.from)
  if (params.to) query.set('to', params.to)
  const headers = { ...getAuthHeaders(), 'X-Region': region }
  const qs = query.toString()

  const tryUrl = async (base: string) => {
    const res = await valtFetch(`${base}?${qs}`, { headers })
    const json = (await res.json().catch(() => ({}))) as any
    return { res, json }
  }

  let res: Response
  let json: any
  const clientAttempt = await tryUrl(`${API_BASE}/client/vehicles/${id}/history`)
  if (clientAttempt.res.ok) {
    res = clientAttempt.res
    json = clientAttempt.json
  } else {
    const legacy = await tryUrl(`${API_BASE}/vehicles/${id}/history`)
    res = legacy.res
    json = legacy.json
  }

  if (!res.ok) {
    throw new Error(json?.message || json?.error || 'Failed to load vehicle history')
  }

  const payload = json?.data != null && typeof json.data === 'object' ? json.data : json
  const log = payload?.log
  const rawItems =
    (log && typeof log === 'object' && !Array.isArray(log) ? log.items : undefined) ??
    payload?.items ??
    (Array.isArray(log) ? log : undefined) ??
    (Array.isArray(payload) ? payload : [])
  const items = Array.isArray(rawItems) ? rawItems.filter((p) => p && typeof p === 'object') : []
  const range = payload?.range as { from?: string; to?: string } | undefined
  return { items, range }
}

/**
 * Admin fleet tracker: GET /vehicles/:id/history?region=… (admin JWT; not for client portal polling).
 * `id` is typically the Go Metro device id (e.g. 862272080744594).
 * Client portal live map should use {@link getClientVehicleHistory} instead.
 * @example GET {VITE_API_URL}/vehicles/862272080744594/history?region=johannesburg
 */
export async function getVehicleHistory(vehicleOrDeviceId: string | number): Promise<ClientVehicleHistoryResult> {
  const region = getRegionForApi()
  const id = encodeURIComponent(String(vehicleOrDeviceId))
  const url = `${API_BASE}/vehicles/${id}/history?region=${region}`
  const res = await valtFetch(url, { headers: getAuthHeaders() })
  const json = (await res.json().catch(() => ({}))) as any
  if (!res.ok) {
    throw new Error(json?.message || json?.error || 'Failed to load vehicle history')
  }
  const rawItems = json?.data?.log?.items ?? json?.data?.items ?? (Array.isArray(json?.data) ? json.data : [])
  const items = Array.isArray(rawItems) ? rawItems : []
  const range = json?.data?.range as { from?: string; to?: string } | undefined
  return { items, range }
}

function readFiniteDriverId(...values: unknown[]): number | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value))) {
      return Number(value)
    }
  }
  return undefined
}

function readDriverDeviceId(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return undefined
}

function postClientDriverPayloadHasAssignment(payload: PostClientDriverPayload): boolean {
  if (payload.mode === 'create') return false
  const fields = payload as Record<string, unknown>
  return (
    fields.valt_vehicle_id != null ||
    fields.vehicle_id != null ||
    fields.bike_id != null ||
    fields.go_metro_bike_id != null ||
    (fields.bike_device_id != null && String(fields.bike_device_id).trim() !== '')
  )
}

function parseClientDriverListPayload(data: unknown): any[] {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []
  const d = data as Record<string, unknown>
  const top = [d.drivers, d.data].find(Array.isArray)
  if (Array.isArray(top)) return top
  if (d.data && typeof d.data === 'object' && !Array.isArray(d.data)) {
    const nested = d.data as Record<string, unknown>
    const nestedList = [nested.drivers, nested.data].find(Array.isArray)
    if (Array.isArray(nestedList)) return nestedList
  }
  return []
}

function parseClientDriverDetailPayload(data: unknown): any | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const d = data as Record<string, unknown>
  const nested = d.data ?? d.driver ?? d.client_driver
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) return nested
  return d
}

function readValtDriverIdFromRow(d: any): string | undefined {
  const valtDriver = d?.valt_driver && typeof d.valt_driver === 'object' ? d.valt_driver : null
  const candidates = [valtDriver?.driver_id, d?.driver_id, d?.valt_driver_id]
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue
    const value = String(candidate).trim()
    if (/^\d+$/.test(value)) continue
    return value
  }
  return undefined
}

function readDriverPhoneFromRow(d: any): string | undefined {
  const valtDriver = d?.valt_driver && typeof d.valt_driver === 'object' ? d.valt_driver : null
  const nested = d?.uber_driver || d?.driver
  const phone =
    d?.phone ??
    d?.phone_number ??
    valtDriver?.phone_number ??
    valtDriver?.phone ??
    nested?.phone_number ??
    nested?.phone
  if (phone == null || String(phone).trim() === '') return undefined
  return String(phone).trim()
}

function buildDriverAssignIdentity(driver?: ClientDriver, valtDriverId?: string): {
  driver_id?: string
  phone_number?: string
} {
  const rawId = valtDriverId?.trim() || (driver ? readValtDriverIdFromRow(driver) : undefined)
  if (rawId && !/^\d+$/.test(rawId)) return { driver_id: rawId }

  const phone = driver ? readDriverPhoneFromRow(driver) : undefined
  if (phone) return { phone_number: phone }

  throw new ClientPortalApiError(
    'Driver must have valt_driver.driver_id or a phone number to assign a vehicle. Do not use client_drivers.id.',
    { status: 400 },
  )
}

/** POST /clients/{id}/drivers/assign — body is driver_id|phone_number + valt_vehicle_id (no mode). */
async function postClientDriverAssignment(body: Record<string, unknown>): Promise<PostClientDriverResult> {
  const assignmentBody = { ...body }
  delete assignmentBody.mode

  const clientId = getClientIdFromStorage()
  if (!clientId) {
    throw new ClientPortalApiError('Missing client account id. Please sign out and sign in again.', {
      status: 401,
    })
  }

  const region = getRegionForApi()
  const urls = [
    buildApiUrlWithQuery(`/clients/${encodeURIComponent(clientId)}/drivers/assign`, { region }),
    buildApiUrlWithQuery(`/clients/${encodeURIComponent(clientId)}/drivers`, { region }),
  ]

  let lastError: ClientPortalApiError | null = null
  for (const url of urls) {
    const res = await valtFetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(assignmentBody),
    })
    const data = (await res.json().catch(() => ({}))) as PostClientDriverResult
    if (res.ok) return data

    const msg = (data as any)?.message || (data as any)?.error || 'Failed to assign vehicle to driver'
    const debug = `POST ${url} body=${JSON.stringify(assignmentBody)}`
    lastError = new ClientPortalApiError(`${msg} (${debug})`, { status: res.status, response: data })
    if (res.status === 400 || res.status === 422) throw lastError
    if (res.status === 404 || res.status === 405) continue
    throw lastError
  }

  throw lastError ?? new ClientPortalApiError('Failed to assign vehicle to driver.', { status: 500 })
}

export function normalizeClientDriverRow(d: any): ClientDriver {
  const valtDriver = d?.valt_driver && typeof d.valt_driver === 'object' ? d.valt_driver : null
  const nested = d?.uber_driver || d?.driver
  const valtVehicle =
    d?.valt_vehicle && typeof d.valt_vehicle === 'object'
      ? d.valt_vehicle
      : d?.vehicle && typeof d.vehicle === 'object'
        ? d.vehicle
        : null
  const bikeNested =
    d?.go_metro_bike && typeof d.go_metro_bike === 'object'
      ? d.go_metro_bike
      : d?.bike && typeof d.bike === 'object'
        ? d.bike
        : null
  const valtDriverId = readValtDriverIdFromRow(d)
  const clientDriverId = d?.client_driver_id ?? d?.client_drivers_id ?? d?.id
  const name =
    d?.name ||
    [d?.first_name, d?.last_name].filter(Boolean).join(' ').trim() ||
    [valtDriver?.first_name, valtDriver?.last_name].filter(Boolean).join(' ').trim() ||
    [nested?.first_name, nested?.last_name].filter(Boolean).join(' ').trim() ||
    'Driver'
  const phone =
    d?.phone ??
    d?.phone_number ??
    valtDriver?.phone_number ??
    valtDriver?.phone ??
    nested?.phone_number ??
    nested?.phone
  const email = d?.email ?? nested?.email
  const valt_vehicle_id = readFiniteDriverId(
    d?.valt_vehicle_id,
    d?.assigned_valt_vehicle_id,
    d?.vehicle_id,
    valtVehicle?.id,
    valtVehicle?.valt_vehicle_id,
  )
  const bike_device_id = readDriverDeviceId(
    d?.bike_device_id,
    d?.bikeDeviceId,
    d?.bike_imei,
    d?.bikeImei,
    bikeNested?.device_id,
    bikeNested?.deviceId,
    bikeNested?.imei,
  )
  const go_metro_bike_id = readFiniteDriverId(
    d?.go_metro_bike_id,
    d?.bike_id,
    bikeNested?.id,
    bikeNested?.go_metro_bike_id,
  )

  const idForUnlink =
    clientDriverId !== undefined && clientDriverId !== null && clientDriverId !== ''
      ? clientDriverId
      : (valtDriverId ?? '')

  return {
    ...d,
    id: idForUnlink,
    client_driver_id: clientDriverId,
    driver_id: valtDriverId != null && valtDriverId !== '' ? String(valtDriverId) : undefined,
    name,
    phone,
    email,
    status: d?.status,
    valt_vehicle_id,
    go_metro_bike_id,
    bike_id: go_metro_bike_id,
    bike_device_id,
  }
}

export type ListClientDriversOptions = {
  /** ValtVehicle primary key — filters to drivers assigned to this vehicle. */
  valt_vehicle_id?: number
  /** Alias for `valt_vehicle_id`. */
  vehicle_id?: number
}

/** List client–driver links (GET /client/drivers). */
export async function listClientDrivers(options?: ListClientDriversOptions): Promise<ClientDriver[]> {
  const query: Record<string, string> = {}
  const vvid = options?.valt_vehicle_id ?? options?.vehicle_id
  if (vvid != null && Number.isFinite(vvid) && vvid > 0) {
    query.valt_vehicle_id = String(vvid)
    query.vehicle_id = String(vvid)
  }
  const region = getRegionForApi()
  if (region) query.region = region

  try {
    const url = buildApiUrlWithQuery('/client/drivers', query)
    const res = await valtFetch(url, { headers: getAuthHeaders() })
    if (!res.ok) return []
    const data = await res.json()
    return parseClientDriverListPayload(data).map((d: any) => normalizeClientDriverRow(d))
  } catch {
    return []
  }
}

/** Driver detail including assignment (GET /client/drivers/:id — client_drivers.id). */
export async function getClientDriver(clientDriversId: string | number): Promise<ClientDriver> {
  const linkId = String(clientDriversId).trim()
  if (!linkId) {
    throw new ClientPortalApiError('Invalid driver id.', { status: 400 })
  }

  const url = buildApiUrlWithQuery(`/client/drivers/${encodeURIComponent(linkId)}`, {
    region: getRegionForApi(),
  })
  const res = await valtFetch(url, { headers: getAuthHeaders() })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = (data as any)?.message || (data as any)?.error || 'Failed to load driver details'
    throw new ClientPortalApiError(msg, { status: res.status, response: data })
  }

  const row = parseClientDriverDetailPayload(data)
  if (!row) {
    throw new ClientPortalApiError('Driver details response was empty.', { status: res.status, response: data })
  }
  return normalizeClientDriverRow(row)
}

/** Link or create a driver (POST /client/drivers). Vehicle assignment uses postClientDriverAssignment. */
export async function postClientDriver(payload: PostClientDriverPayload): Promise<PostClientDriverResult> {
  if (payload.mode !== 'create' && postClientDriverPayloadHasAssignment(payload)) {
    const body: Record<string, unknown> = { ...payload }
    delete body.mode
    const vvid = body.valt_vehicle_id ?? body.vehicle_id
    if (vvid != null && body.valt_vehicle_id == null) body.valt_vehicle_id = vvid
    return postClientDriverAssignment(body)
  }

  const body: Record<string, unknown> =
    payload.mode === 'create' ? { ...payload } : { ...payload, mode: 'link' as const }

  const url = buildApiUrlWithQuery('/client/drivers', { region: getRegionForApi() })

  const res = await valtFetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as PostClientDriverResult
  if (!res.ok) {
    const msg = (data as any)?.message || (data as any)?.error || 'Failed to save driver assignment'
    throw new ClientPortalApiError(msg, { status: res.status, response: data })
  }
  return data
}

/** Remove client–driver link (DELETE /client/drivers/:id — id = client_drivers.id from the list). */
export async function unlinkClientDriver(clientDriversId: string | number): Promise<void> {
  const res = await valtFetch(`${API_BASE}/client/drivers/${clientDriversId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const msg = (data as any)?.message || (data as any)?.error || 'Failed to remove driver link'
    throw new ClientPortalApiError(msg, { status: res.status, response: data })
  }
}

/**
 * Unassign driver from vehicle for the logged-in client (DELETE /clients/:clientId/drivers/:valtVehicleId).
 * Matches admin route shape; requires contractor profile `id` in localStorage (same as listClientVehicles).
 */
export async function unlinkClientVehicleFromDriver(valtVehicleId: number): Promise<void> {
  const clientId = getClientIdFromStorage()
  if (!clientId) {
    throw new ClientPortalApiError('Missing client account id. Please sign out and sign in again.', {
      status: 401,
    })
  }
  if (!Number.isFinite(valtVehicleId)) {
    throw new ClientPortalApiError('Invalid vehicle id.', { status: 400 })
  }
  const region = getRegionForApi()
  const path = `${API_BASE}/clients/${encodeURIComponent(clientId)}/drivers/${encodeURIComponent(String(valtVehicleId))}`
  const url = `${path}?region=${encodeURIComponent(region)}`
  const res = await valtFetch(url, { method: 'DELETE', headers: getAuthHeaders() })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = (data as any)?.message || (data as any)?.error || 'Failed to unassign vehicle from driver'
    throw new ClientPortalApiError(msg, { status: res.status, response: data })
  }
}

/** Clear asset assignment — POST driver_id|phone_number without valt_vehicle_id. */
export async function clearClientDriverAssetAssignment(driver: ClientDriver): Promise<PostClientDriverResult> {
  const identity = buildDriverAssignIdentity(driver)
  return postClientDriverAssignment(identity)
}

/** Clear vehicle assignment: link driver without valt_vehicle_id (driver stays on the client). */
export async function clearClientDriverValtVehicle(uberDriverId: string): Promise<PostClientDriverResult> {
  return postClientDriver({ mode: 'link', driver_id: uberDriverId })
}

/** Assign driver to valt vehicle — POST with driver_id (UUID) or phone_number + valt_vehicle_id. */
export async function assignClientVehicleDriver(params: {
  vehicleId: string | number
  driverId?: string | number
  driver?: ClientDriver
}): Promise<ClientVehicleAssignmentResponse> {
  const vid = Number(params.vehicleId)
  if (!Number.isFinite(vid)) throw new ClientPortalApiError('Invalid vehicle id.', { status: 400 })

  const identity = buildDriverAssignIdentity(
    params.driver,
    params.driverId != null ? String(params.driverId) : undefined,
  )

  return postClientDriverAssignment({
    ...identity,
    valt_vehicle_id: vid,
  }) as Promise<ClientVehicleAssignmentResponse>
}

/** Unassign driver from vehicle via DELETE /clients/{clientId}/drivers/{valtVehicleId}. */
export async function unassignClientVehicleDriver(vehicleId: string | number): Promise<void> {
  const vid = Number(vehicleId)
  if (!Number.isFinite(vid)) throw new Error('Invalid vehicle id')
  await unlinkClientVehicleFromDriver(vid)
}

/** Assign driver to bike — same assign endpoint shape with bike_id / bike_device_id. */
export async function assignClientBikeDriver(params: {
  driverId?: string | number
  driver?: ClientDriver
  bikeId?: number
  bikeDeviceId?: string
}): Promise<PostClientDriverResult> {
  const identity = buildDriverAssignIdentity(
    params.driver,
    params.driverId != null ? String(params.driverId) : undefined,
  )

  const body: Record<string, unknown> = { ...identity }
  if (params.bikeId != null && Number.isFinite(params.bikeId)) {
    body.bike_id = params.bikeId
    body.go_metro_bike_id = params.bikeId
  }
  const imei = params.bikeDeviceId != null ? String(params.bikeDeviceId).trim() : ''
  if (imei) body.bike_device_id = imei
  if (body.bike_id == null && !body.bike_device_id) {
    throw new ClientPortalApiError('Invalid bike assignment — provide bike_id or bike_device_id.', { status: 400 })
  }
  return postClientDriverAssignment(body)
}

/**
 * Remove driver↔bike link.
 * DELETE /clients/{clientId}/drivers/bikes/{bikeId} or .../drivers/bike/{imei}
 */
export async function unassignClientBikeDriver(params: {
  bikeId?: number
  bikeDeviceId?: string
}): Promise<void> {
  const clientId = getClientIdFromStorage()
  if (!clientId) {
    throw new ClientPortalApiError('Missing client account id. Please sign out and sign in again.', {
      status: 401,
    })
  }
  const region = getRegionForApi()
  const registryId = params.bikeId != null && Number.isFinite(params.bikeId) ? Number(params.bikeId) : null
  const imei = params.bikeDeviceId != null ? String(params.bikeDeviceId).trim() : ''
  let url = ''
  if (registryId != null) {
    url = `${API_BASE}/clients/${encodeURIComponent(clientId)}/drivers/bikes/${encodeURIComponent(String(registryId))}?region=${encodeURIComponent(region)}`
  } else if (imei) {
    url = `${API_BASE}/clients/${encodeURIComponent(clientId)}/drivers/bike/${encodeURIComponent(imei)}?region=${encodeURIComponent(region)}`
  } else {
    throw new ClientPortalApiError('Invalid bike reference — provide bikeId or bikeDeviceId.', { status: 400 })
  }
  const res = await valtFetch(url, { method: 'DELETE', headers: getAuthHeaders() })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = (data as any)?.message || (data as any)?.error || 'Failed to unassign driver from bike'
    throw new ClientPortalApiError(msg, { status: res.status, response: data })
  }
}

/** Assign vehicle to driver — uses valt_driver.driver_id from the driver row, not client_drivers.id. */
export async function assignClientDriverVehicle(params: {
  valtDriverId?: string
  /** @deprecated use valtDriverId */
  uberDriverId?: string
  driver?: ClientDriver
  vehicleId: string | number
}): Promise<ClientVehicleAssignmentResponse> {
  const vid = Number(params.vehicleId)
  if (!Number.isFinite(vid)) throw new ClientPortalApiError('Invalid vehicle id.', { status: 400 })

  const identity = buildDriverAssignIdentity(params.driver, params.valtDriverId ?? params.uberDriverId)

  return postClientDriverAssignment({
    ...identity,
    valt_vehicle_id: vid,
  }) as Promise<ClientVehicleAssignmentResponse>
}

/**
 * Remove vehicle assignment from this driver (driver remains linked to client).
 * Uses DELETE /clients/{clientId}/drivers/{valtVehicleId} when the driver row includes valt_vehicle_id.
 */
export async function unassignClientDriverVehicle(uberDriverId: string): Promise<void> {
  const drivers = await listClientDrivers()
  const row = drivers.find((d) => String(d.driver_id) === String(uberDriverId))
  const vvid = row?.valt_vehicle_id
  if (vvid == null || !Number.isFinite(Number(vvid))) {
    throw new ClientPortalApiError('This driver has no vehicle assigned.', { status: 400 })
  }
  await unlinkClientVehicleFromDriver(Number(vvid))
}

/** Section keys returned by GET /api/client/reports (combined payload). */
export const CLIENT_FLEET_REPORT_TYPES = [
  'fleet_summary',
  'vehicle_utilisation',
  'driver_activity',
  'energy_consumption',
  'fleet_cost',
] as const

export type ClientFleetReportType = (typeof CLIENT_FLEET_REPORT_TYPES)[number]

export function isClientFleetReportType(v: string): v is ClientFleetReportType {
  const n = v.replace(/-/g, '_')
  return (CLIENT_FLEET_REPORT_TYPES as readonly string[]).includes(n)
}

export function normalizeClientFleetReportType(v: string): ClientFleetReportType {
  const n = v.replace(/-/g, '_')
  if (isClientFleetReportType(n)) return n
  return 'vehicle_utilisation'
}

/** Generic trip/summary report for the client (date-range based). */
export async function getClientSummaryReport(payload: {
  from: string
  to: string
  /** e.g. vehicle_utilisation | driver_activity | energy_consumption | fleet_cost */
  reportType?: string
  region?: string
}): Promise<any> {
  const type = normalizeClientFleetReportType(payload.reportType ?? 'vehicle_utilisation')
  return getClientFleetReportByType({
    type,
    startDate: payload.from,
    endDate: payload.to,
    region: payload.region,
  })
}

export interface ClientFleetReportRange {
  start_date?: string
  end_date?: string
  [key: string]: unknown
}

export interface ClientFleetSingleReportResponse {
  client?: unknown
  client_connection?: unknown
  vehicle_connection?: unknown
  report_type?: ClientFleetReportType
  range?: ClientFleetReportRange
  rows?: Record<string, unknown>[]
  meta?: {
    total_rows?: number
    assignment_storage?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

export interface ClientFleetCombinedReportResponse {
  client?: unknown
  client_connection?: unknown
  vehicle_connection?: unknown
  assignment_storage?: string
  range?: ClientFleetReportRange
  fleet_summary?: Record<string, unknown>[]
  vehicle_utilisation?: Record<string, unknown>[]
  driver_activity?: Record<string, unknown>[]
  energy_consumption?: Record<string, unknown>[]
  fleet_cost?: Record<string, unknown>[]
  [key: string]: unknown
}

function toYmdIfIsoString(v?: string): string | undefined {
  if (!v) return undefined
  const trimmed = v.trim()
  if (!trimmed) return undefined
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
  const d = new Date(trimmed)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toISOString().slice(0, 10)
}

function buildClientReportUrl(path: string, opts?: { startDate?: string; endDate?: string; region?: string }): string {
  const q = new URLSearchParams()
  const start = toYmdIfIsoString(opts?.startDate)
  const end = toYmdIfIsoString(opts?.endDate)
  if (start) q.set('start_date', start)
  if (end) q.set('end_date', end)
  q.set('region', opts?.region ?? getRegionForApi())
  return `${API_BASE}${path}?${q.toString()}`
}

export async function getClientFleetReportCombined(options?: {
  startDate?: string
  endDate?: string
  region?: string
}): Promise<ClientFleetCombinedReportResponse> {
  const res = await valtFetch(buildClientReportUrl('/client/reports', options), {
    headers: getAuthHeaders(),
  })
  const data = (await res.json().catch(() => ({}))) as ClientFleetCombinedReportResponse
  if (!res.ok) {
    throw new Error((data as any)?.message || (data as any)?.error || 'Failed to fetch client fleet reports')
  }
  return data
}

export async function getClientFleetReportByType(options: {
  type: ClientFleetReportType | string
  startDate?: string
  endDate?: string
  region?: string
}): Promise<ClientFleetSingleReportResponse> {
  const type = normalizeClientFleetReportType(String(options.type))
  const url = buildClientReportUrl(`/client/reports/${encodeURIComponent(type)}`, options)
  const res = await valtFetch(url, { headers: getAuthHeaders() })
  const data = (await res.json().catch(() => ({}))) as ClientFleetSingleReportResponse
  if (res.ok) return data

  // Temporary resilience: if single-section endpoint is unavailable upstream, fall back
  // to combined payload endpoint and extract the requested section.
  if (res.status === 503 || res.status === 404) {
    const combined = await getClientFleetReportCombined(options)
    const section = combined?.[type]
    if (Array.isArray(section)) {
      return {
        client: combined.client,
        client_connection: combined.client_connection,
        vehicle_connection: combined.vehicle_connection,
        report_type: type,
        range: combined.range,
        rows: section,
        meta: {
          total_rows: section.length,
          assignment_storage: combined.assignment_storage,
        },
      }
    }
  }

  const allowed = Array.isArray((data as any)?.allowed_types)
    ? ` Allowed types: ${(data as any).allowed_types.join(', ')}.`
    : ''
  throw new Error(((data as any)?.message || (data as any)?.error || 'Failed to fetch client report') + allowed)
}

/** Presets for GET /api/client/odometer-summary (optional `preset` query). */
export const CLIENT_ODOMETER_SUMMARY_PRESETS = [
  'today',
  'yesterday',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'all',
] as const

export type ClientOdometerSummaryPreset = (typeof CLIENT_ODOMETER_SUMMARY_PRESETS)[number] | 'custom'

export interface ClientOdometerReportDeviceRow {
  device_id?: string | number | null
  device_name?: string | null
  license_plate?: string | null
  model?: string | null
  valt_vehicle_id?: number | null
  number_plate?: string | null
  vin?: string | null
  start_odometer?: number | null
  end_odometer?: number | null
  start_odometer_m?: number | null
  end_odometer_m?: number | null
  kilometers_travelled?: number | null
  distance_km?: number | null
  readings_count?: number | null
  trips?: number | null
  /** `vehicle_data` (today/live) or `vehicle_data_summary` (historical) */
  source?: 'vehicle_data' | 'vehicle_data_summary' | string | null
  [key: string]: unknown
}

export interface ClientOdometerReportByDate {
  date?: string
  vehicles?: ClientOdometerReportDeviceRow[]
  [key: string]: unknown
}

export interface ClientOdometerReportByHour {
  hour: number
  kilometers_travelled?: number | null
  readings_count?: number | null
}

export interface ClientOdometerReportResponse {
  success?: boolean
  connection?: string
  /** Top-level source for the window: live today vs summary */
  source?: 'vehicle_data' | 'vehicle_data_summary' | string | null
  window?: {
    start?: string
    end?: string
    [key: string]: unknown
  }
  count?: number
  by_date?: ClientOdometerReportByDate[] | Record<string, ClientOdometerReportDeviceRow[]>
  /** Fleet-wide hour-of-day totals (present when `include_hourly` was requested) */
  by_hour?: ClientOdometerReportByHour[]
  data?: ClientOdometerReportDeviceRow[]
  message?: string
  errors?: Record<string, string[]>
  [key: string]: unknown
}

export interface ClientBikeOdometerReportDeviceRow {
  device_id?: string | number | null
  go_metro_bike_id?: number | null
  name?: string | null
  license_plate?: string | null
  model?: string | null
  kilometers_travelled?: number | null
  gps_segments_count?: number | null
  [key: string]: unknown
}

export interface ClientBikeOdometerReportByDate {
  date?: string
  bikes?: ClientBikeOdometerReportDeviceRow[]
  [key: string]: unknown
}

export interface ClientBikeOdometerReportResponse {
  success?: boolean
  type?: 'bike'
  connection?: string
  window?: {
    start?: string
    end?: string
    [key: string]: unknown
  }
  count?: number
  by_date?: ClientBikeOdometerReportByDate[] | Record<string, ClientBikeOdometerReportDeviceRow[]>
  data?: ClientBikeOdometerReportDeviceRow[]
  message?: string
  errors?: Record<string, string[]>
  [key: string]: unknown
}

export interface ClientChargingSessionRow {
  id?: number | string
  aeversa_id?: string | null
  id_tag?: string | null
  charger_name?: string | null
  connector_name?: string | null
  session_start?: string | null
  session_end?: string | null
  energy_kwh?: number | null
  session_cost?: number | null
  active?: boolean
  [key: string]: unknown
}

export interface ClientChargingReportVehicleRow {
  vehicle_id?: number | string
  aeversa_id_tag?: string | null
  number_plate?: string | null
  model?: string | null
  sessions_count?: number
  active_sessions_count?: number
  total_duration_minutes?: number
  total_energy_kwh?: number
  cost_to_company_zar?: number
  charge_to_driver_zar?: number
  sessions?: ClientChargingSessionRow[]
  daily_breakdown?: Array<{
    charge_date?: string
    number_of_sessions?: number
    total_energy_kwh?: number
    sessions?: ClientChargingSessionRow[]
    [key: string]: unknown
  }>
  [key: string]: unknown
}

export interface ClientChargingReportByDate {
  date?: string
  vehicles?: ClientChargingReportVehicleRow[]
  [key: string]: unknown
}

export interface ClientChargingReportError {
  vehicle_id?: number | string
  number_plate?: string
  error?: string
  plate_key?: string
  [key: string]: unknown
}

export interface ClientChargingReportResponse {
  success?: boolean
  connection?: string
  region?: string
  window?: {
    start?: string
    end?: string
    [key: string]: unknown
  }
  count?: number
  by_date?: ClientChargingReportByDate[]
  data?: ClientChargingReportVehicleRow[]
  errors?: ClientChargingReportError[]
  message?: string
  [key: string]: unknown
}

export interface ClientOdometerSummaryVehicle {
  valt_vehicle_id?: number
  number_plate?: string | null
  vin?: string | null
  device_id?: string | number | null
  distance_km?: number | null
  start_odometer_m?: number | null
  end_odometer_m?: number | null
  source?: string
  [key: string]: unknown
}

export interface ClientOdometerSummaryRange {
  start_date?: string
  end_date?: string
  start_datetime?: string
  end_datetime?: string
  [key: string]: unknown
}

export interface ClientOdometerSummaryTotals {
  distance_km?: number
  vehicles_assigned?: number
  vehicles_with_summary_or_telemetry?: number
  [key: string]: unknown
}

/** Single-window response from GET /api/client/odometer-summary (not `preset=all`). */
export interface ClientOdometerSummaryWindow {
  preset?: string
  range?: ClientOdometerSummaryRange
  vehicles?: ClientOdometerSummaryVehicle[]
  totals?: ClientOdometerSummaryTotals
  assignment_storage?: string
  [key: string]: unknown
}

/** GET /api/client/odometer-summary — JSON envelope (single window, custom range, or `preset=all`). */
export type ClientOdometerSummaryResponse = {
  success?: boolean
  client?: unknown
  client_connection?: unknown
  vehicle_connection?: unknown
  preset?: string
  presets_available?: string[]
  range?: ClientOdometerSummaryRange
  vehicles?: ClientOdometerSummaryVehicle[]
  totals?: ClientOdometerSummaryTotals
  assignment_storage?: string
  /** When preset is `all`, each key mirrors a single-window slice. */
  data?: Record<string, ClientOdometerSummaryWindow>
  message?: string
  errors?: Record<string, string[]>
  [key: string]: unknown
}

function buildClientOdometerSummaryUrl(opts?: {
  preset?: string
  startDate?: string
  endDate?: string
  region?: string
}): string {
  const q = new URLSearchParams()
  q.set('region', opts?.region ?? getRegionForApi())
  const start = toYmdIfIsoString(opts?.startDate)
  const end = toYmdIfIsoString(opts?.endDate)
  if (start && end) {
    q.set('start_date', start)
    q.set('end_date', end)
  } else {
    const p = (opts?.preset ?? 'this_month').trim()
    q.set('preset', p || 'this_month')
  }
  return `${API_BASE}/client/odometer-summary?${q.toString()}`
}

/**
 * Fleet odometer / distance summary by device for the logged-in client.
 * GET /api/client/odometer-summary — optional `preset`, or `start_date`+`end_date` (preset ignored).
 */
export async function getClientOdometerSummary(options?: {
  preset?: string
  startDate?: string
  endDate?: string
  region?: string
}): Promise<ClientOdometerSummaryResponse> {
  const url = buildClientOdometerSummaryUrl(options)
  const res = await valtFetch(url, { headers: getAuthHeaders() })
  const data = (await res.json().catch(() => ({}))) as ClientOdometerSummaryResponse
  if (!res.ok) {
    const msg =
      typeof data.message === 'string'
        ? data.message
        : (data as { error?: string }).error || `Failed to fetch odometer summary (${res.status})`
    throw new Error(msg)
  }
  return data
}

/** @deprecated Prefer {@link getClientOdometerSummary} — maps legacy `from`/`to` to start_date/end_date. */
export async function getClientOdometerReport(payload: {
  from: string
  to: string
  region?: string
}): Promise<ClientOdometerSummaryResponse> {
  return getClientOdometerSummary({
    startDate: payload.from,
    endDate: payload.to,
    region: payload.region,
  })
}

/**
 * Odometer report generation.
 * - Client JWT path (default): POST /api/client/odometer-report
 * - Staff/admin JWT path (when `clientId` is provided): POST /api/clients/{id}/odometer-report
 */
export async function generateClientOdometerReport(options?: {
  clientId?: string | number
  preset?: 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month'
  startDate?: string
  endDate?: string
  region?: string
  includeHourly?: boolean
  /** Single Go Metro device id — scopes `data`, `by_date`, and `by_hour`. */
  deviceId?: string
  /** Multiple Go Metro device ids — same scope as `deviceId`. */
  deviceIds?: string[]
}): Promise<ClientOdometerReportResponse> {
  const body: Record<string, unknown> = {}
  if (options?.preset) body.preset = options.preset
  const startDate = toYmdIfIsoString(options?.startDate)
  const endDate = toYmdIfIsoString(options?.endDate)
  if (startDate) body.start_date = startDate
  if (endDate) body.end_date = endDate
  body.region = options?.region ?? getRegionForApi()
  if (options?.includeHourly) body.include_hourly = true
  if (options?.deviceId) body.device_id = options.deviceId
  if (options?.deviceIds?.length) body.device_ids = options.deviceIds

  const headers = {
    ...getAuthHeaders(),
    'X-Region': String(body.region),
  }

  const explicitId = options?.clientId
  const hasExplicitClientId = explicitId !== null && explicitId !== undefined && String(explicitId).trim() !== ''
  const url = hasExplicitClientId
    ? `${API_BASE}/clients/${encodeURIComponent(String(explicitId))}/odometer-report`
    : `${API_BASE}/client/odometer-report`

  const res = await valtFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as ClientOdometerReportResponse
  if (!res.ok) {
    throw new Error(
      data.message || (data as { error?: string }).error || `Failed to generate odometer report (${res.status})`,
    )
  }
  return data
}

/**
 * Bike odometer report generation (Haversine distance from GPS segments).
 * - Client JWT path (default): POST /api/client/bike-odometer-report
 * - Staff/admin JWT path (when `clientId` is provided): POST /api/clients/{id}/bike-odometer-report
 */
export async function generateClientBikeOdometerReport(options?: {
  clientId?: string | number
  preset?: 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month'
  startDate?: string
  endDate?: string
  region?: string
}): Promise<ClientBikeOdometerReportResponse> {
  const body: Record<string, string> = {}
  if (options?.preset) body.preset = options.preset
  const startDate = toYmdIfIsoString(options?.startDate)
  const endDate = toYmdIfIsoString(options?.endDate)
  if (startDate) body.start_date = startDate
  if (endDate) body.end_date = endDate
  body.region = options?.region ?? getRegionForApi()

  const headers = {
    ...getAuthHeaders(),
    'X-Region': body.region,
  }

  const explicitId = options?.clientId
  const hasExplicitClientId = explicitId !== null && explicitId !== undefined && String(explicitId).trim() !== ''
  const url = hasExplicitClientId
    ? `${API_BASE}/clients/${encodeURIComponent(String(explicitId))}/bike-odometer-report?region=${encodeURIComponent(body.region)}`
    : `${API_BASE}/client/bike-odometer-report`

  const res = await valtFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as ClientBikeOdometerReportResponse
  if (!res.ok) {
    throw new Error(
      data.message || (data as { error?: string }).error || `Failed to generate bike odometer report (${res.status})`,
    )
  }
  return data
}

/**
 * Charging report generation.
 * - Client JWT path (default): POST /api/client/charging-report
 * - Staff/admin JWT path (when `clientId` is provided): POST /api/clients/{id}/charging-report
 */
export async function generateClientChargingReport(options?: {
  clientId?: string | number
  preset?: 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month'
  startDate?: string
  endDate?: string
  region?: string
}): Promise<ClientChargingReportResponse> {
  const body: Record<string, string> = {}
  if (options?.preset) body.preset = options.preset
  const startDate = toYmdIfIsoString(options?.startDate)
  const endDate = toYmdIfIsoString(options?.endDate)
  if (startDate) body.start_date = startDate
  if (endDate) body.end_date = endDate
  body.region = options?.region ?? getRegionForApi()

  const headers = {
    ...getAuthHeaders(),
    'X-Region': body.region,
  }

  const explicitId = options?.clientId
  const hasExplicitClientId = explicitId !== null && explicitId !== undefined && String(explicitId).trim() !== ''
  const url = hasExplicitClientId
    ? `${API_BASE}/clients/${encodeURIComponent(String(explicitId))}/charging-report`
    : `${API_BASE}/client/charging-report`

  const res = await valtFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as ClientChargingReportResponse
  if (!res.ok) {
    throw new Error(
      data.message || (data as { error?: string }).error || `Failed to generate charging report (${res.status})`,
    )
  }
  return data
}

export interface ClientSignalEventRow {
  device_id: string
  event_type: 'charging' | 'ignition' | 'movement'
  ts: number
  occurred_at: string
  status_change: string
  current_status: string | null
  prev_status: string | null
  change_in_soc: number | null
  km_travelled: number | null
  duration_sec: number | null
  /** Asset registration when device is linked to a vehicle. */
  number_plate?: string | null
  soc?: number | null
  prev_soc?: number | null
  odometer?: number | null
  valt_vehicle_id?: number | null
  client_id?: number | null
}

export interface ClientSignalEventsSummary {
  total_events: number
  charging: number
  ignition: number
  stops: number
  avg_stop_sec: number | null
  vehicles: number
}

export interface ClientSignalEventsGraphPoint {
  bucket_start: string
  charging: number
  ignition: number
  movement: number
}

export interface ClientSignalEventsGraph {
  bucket: 'hour' | 'day'
  points: ClientSignalEventsGraphPoint[]
}

export interface ClientSignalEventsPagination {
  current_page: number
  per_page: number
  total: number
  last_page: number
  from: number | null
  to: number | null
}

export interface ClientSignalEventsReportResponse {
  success: boolean
  connection?: string
  source?: string
  timezone?: string
  window?: { start: string; end: string }
  filters?: Record<string, unknown>
  summary?: ClientSignalEventsSummary
  graph?: ClientSignalEventsGraph
  data?: ClientSignalEventRow[]
  pagination?: ClientSignalEventsPagination
  shifts?: Array<Record<string, unknown>>
  message?: string
}

/** POST /api/client/signal-events-report (or /api/clients/{id}/signal-events-report for staff) */
export async function generateClientSignalEventsReport(options?: {
  clientId?: string | number
  preset?: string
  startDate?: string
  endDate?: string
  start?: string
  end?: string
  deviceId?: string
  deviceIds?: string[]
  eventType?: 'charging' | 'ignition' | 'movement'
  movementStatus?: 'Stopped' | 'Idle' | 'Moving' | 'Off'
  includeShifts?: boolean
  include?: Array<'summary' | 'graph' | 'details'>
  page?: number
  perPage?: number
}): Promise<ClientSignalEventsReportResponse> {
  const body: Record<string, unknown> = {}
  if (options?.preset) body.preset = options.preset
  if (options?.startDate) body.start_date = options.startDate
  if (options?.endDate) body.end_date = options.endDate
  if (options?.start) body.start = options.start
  if (options?.end) body.end = options.end
  if (options?.deviceId) body.device_id = options.deviceId
  if (options?.deviceIds?.length) body.device_ids = options.deviceIds
  if (options?.eventType) body.event_type = options.eventType
  if (options?.movementStatus) body.movement_status = options.movementStatus
  if (options?.includeShifts) body.include_shifts = true
  // API expects comma-separated list (same as fleet GET ?include=summary,graph,details).
  if (options?.include?.length) body.include = options.include.join(',')
  if (options?.page != null) body.page = options.page
  if (options?.perPage != null) body.per_page = Math.min(200, Math.max(1, options.perPage))
  body.region = getRegionForApi()

  const headers = {
    ...getAuthHeaders(),
    'Content-Type': 'application/json',
    'X-Region': String(body.region),
  }

  const explicitId = options?.clientId
  const hasExplicitClientId = explicitId !== null && explicitId !== undefined && String(explicitId).trim() !== ''
  const url = hasExplicitClientId
    ? `${API_BASE}/clients/${encodeURIComponent(String(explicitId))}/signal-events-report`
    : `${API_BASE}/client/signal-events-report`

  const res = await valtFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as ClientSignalEventsReportResponse
  if (!res.ok) {
    throw new Error(data.message || `Failed to load vehicle activity (${res.status})`)
  }
  return data
}

/** Charging report for the client (EV sessions, date range). */
export async function getClientChargingReport(payload: { from: string; to: string }): Promise<any> {
  const region = getRegionForApi()
  const res = await valtFetch(`${API_BASE}/client/reports/charging?region=${region}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data.message || data.error || 'Failed to fetch charging report')
  }
  return data
}

/** Row from GET /bikes — fleet bike with latest telemetry. */
export interface ClientBike {
  /** go_metro_bikes registry id */
  id?: number
  bike_id?: number
  go_metro_bike_id?: number
  deviceId: string
  name: string
  type?: string
  model?: string
  status?: string
  cell?: string
  lat?: number
  lng?: number
  timestamp?: number
  /** Speed in km/h from device API. */
  speed?: number
  ignitionStatus?: boolean
  movementStatus?: boolean
  isMoving?: boolean
  pollInterval?: number
  batteryLevel?: number
  batteryVoltage?: number
  batteryCurrent?: number | null
  canSoc1?: number
  canSoc2?: number
  canRemainingRange?: number
  supplyVoltage?: number
  voltage?: number
  current?: number
  direction?: number
  altitude?: number
  manufacturer?: string | null
  ident?: string
  number_plate?: string
  licensePlate?: string
  license_plate?: string
  /** False when assigned without a telematics device — still listed, not live-trackable. */
  has_telemetry_device?: boolean
  [key: string]: unknown
}

function readBoolField(row: any, ...keys: string[]): boolean | undefined {
  for (const k of keys) {
    const v = row?.[k]
    if (v === true || v === 1) return true
    if (v === false || v === 0) return false
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase()
      if (s === 'true' || s === 'on' || s === '1') return true
      if (s === 'false' || s === 'off' || s === '0') return false
    }
  }
  return undefined
}

/** Normalize list or history telemetry row into {@link ClientBike} shape. */
export function normalizeClientBikeTelemetry(row: any): ClientBike | null {
  if (!row || typeof row !== 'object') return null
  const deviceId = String(row.deviceId ?? row.ident ?? row.device_id ?? '').trim()
  if (!deviceId) return null
  const speedKmh = readNumber(row, 'speed', 'canVehicleSpeed', 'can_vehicle_speed')
  const movement = readBoolField(row, 'movement', 'movementStatus', 'movement_status')
  const ignition = readBoolField(row, 'ignition', 'ignitionStatus', 'ignition_status')
  const batteryLevel =
    readNumber(row, 'batteryLevel', 'battery_level', 'batteryPercent', 'battery_percent') ?? undefined
  const canSoc1 = readBikeCanSoc1(row) ?? undefined
  const canSoc2 = readBikeCanSoc2(row) ?? undefined
  const canRemainingRange = readBikeRemainingRangeKm(row) ?? undefined
  const isMovingExplicit = row.isMoving === true
  const isMoving =
    isMovingExplicit ||
    movement === true ||
    (speedKmh != null && speedKmh > 3)
  return withNormalizedBikeIdentityFields({
    ...row,
    deviceId,
    ident: row.ident != null ? String(row.ident) : deviceId,
    name: String(row.name ?? deviceId),
    type: row.type != null ? String(row.type) : 'bike',
    model: row.model != null ? String(row.model) : undefined,
    status: row.status != null ? String(row.status) : undefined,
    lat: readNumber(row, 'latitude', 'lat') ?? undefined,
    lng: readNumber(row, 'longitude', 'lng', 'lon', 'long') ?? undefined,
    timestamp: readNumber(row, 'timestamp') ?? undefined,
    speed: speedKmh ?? undefined,
    isMoving,
    movementStatus: movement ?? false,
    ignitionStatus: ignition ?? false,
    pollInterval: readNumber(row, 'pollInterval', 'poll_interval') ?? undefined,
    batteryLevel,
    batteryVoltage: readNumber(row, 'batteryVoltage', 'battery_voltage', 'voltage') ?? undefined,
    batteryCurrent: readNumber(row, 'current', 'batteryCurrent', 'battery_current') ?? undefined,
    canSoc1,
    canSoc2,
    canRemainingRange,
    supplyVoltage: readNumber(row, 'supplyVoltage', 'supply_voltage') ?? undefined,
    voltage: readNumber(row, 'voltage') ?? undefined,
    current: readNumber(row, 'current') ?? undefined,
    direction: readNumber(row, 'direction', 'heading') ?? undefined,
    altitude: readNumber(row, 'altitude', 'alt') ?? undefined,
    has_telemetry_device:
      typeof row.has_telemetry_device === 'boolean' ? row.has_telemetry_device : true,
  })
}

function parseClientBikeList(data: unknown): ClientBike[] {
  const list = Array.isArray(data) ? data : (data as any)?.data || (data as any)?.bikes || []
  if (!Array.isArray(list)) return []
  return list
    .map((row: any) => normalizeClientBikeTelemetry(row))
    .filter((b: ClientBike | null): b is ClientBike => !!b && !!b.deviceId)
}

function legacyBikeListUrl(details: boolean, clientId: string | null, region: string): string {
  if (details) {
    return clientId
      ? `${API_BASE}/clients/${encodeURIComponent(clientId)}/bikes-details?region=${encodeURIComponent(region)}`
      : `${API_BASE}/client/bikes-details?region=${encodeURIComponent(region)}`
  }
  return clientId
    ? `${API_BASE}/clients/${encodeURIComponent(clientId)}/bikes?region=${encodeURIComponent(region)}`
    : `${API_BASE}/client/bikes?region=${encodeURIComponent(region)}`
}

/** `unavailable` when route is gone (single-schema); otherwise legacy bike rows. */
async function tryFetchLegacyClientBikes(details: boolean): Promise<ClientBike[] | 'unavailable'> {
  if (SINGLE_SCHEMA_ENABLED) return 'unavailable'
  const region = getRegionForApi()
  const headers = { ...getAuthHeaders(), 'X-Region': region }
  const clientId = getClientIdFromStorage()
  const res = await valtFetch(legacyBikeListUrl(details, clientId, region), { headers })
  if (res.status === 404) return 'unavailable'
  if (!res.ok) return []
  const data = await res.json()
  return parseClientBikeList(data)
}

function unifiedClientBikesFromAssets(assets: ClientVehicle[]): ClientBike[] {
  return assets
    .filter((asset) => isBikeAssetType(readClientAssetType(asset)))
    .map((asset) => clientVehicleToClientBike(asset))
    .filter((bike): bike is ClientBike => !!bike)
}

/** Map unified asset row to bike telemetry shape (device id is the tracker key). */
export function clientVehicleToClientBike(asset: ClientVehicle): ClientBike | null {
  const telemetryDeviceId = resolveAssetDeviceId(asset)
  const fallbackId = String(asset.id || '').trim()
  // Keep bikes without a telemetry device in the assigned list (inactive / not trackable).
  const listId = telemetryDeviceId || fallbackId
  if (!listId) return null
  const registryId =
    asset.go_metro_bike_id != null && Number.isFinite(Number(asset.go_metro_bike_id))
      ? Number(asset.go_metro_bike_id)
      : undefined
  const hasTelemetry = telemetryDeviceId != null
  return normalizeClientBikeTelemetry({
    ...asset,
    deviceId: listId,
    device_id: listId,
    ident: asset.ident != null ? String(asset.ident) : listId,
    id: registryId,
    bike_id: registryId,
    go_metro_bike_id: registryId,
    name: asset.name || asset.number_plate || listId,
    type: readClientAssetType(asset),
    model: asset.model != null ? String(asset.model) : undefined,
    status: hasTelemetry ? (asset.status != null ? String(asset.status) : undefined) : 'inactive',
    number_plate: asset.number_plate,
    lat: asset.lat,
    lng: asset.lng,
    speed: asset.speed,
    direction: asset.heading,
    ignitionStatus: asset.last_ignition_status,
    soc: asset.soc,
    canSoc1: asset.canSoc1 ?? readBikeCanSoc1(asset),
    canSoc2: asset.canSoc2 ?? readBikeCanSoc2(asset),
    canRemainingRange: readBikeRemainingRangeKm(asset) ?? asset.range_km_max,
    supplyVoltage: readNumber(asset, 'supplyVoltage', 'supply_voltage'),
    batteryLevel: readNumber(asset, 'batteryLevel', 'battery_level', 'batteryPercent', 'battery_percent'),
    has_telemetry_device: hasTelemetry,
  })
}

/** List bikes assigned to the client fleet. Legacy bike routes, then unified /vehicles filtered by type. */
export async function listClientBikes(): Promise<ClientBike[]> {
  try {
    const legacy = await tryFetchLegacyClientBikes(false)
    if (legacy !== 'unavailable') return legacy
    return unifiedClientBikesFromAssets(await listClientAssets())
  } catch {
    return []
  }
}

/** Registry + telemetry for assigned client bikes. */
export async function listClientBikesDetails(): Promise<ClientBike[]> {
  try {
    const legacy = await tryFetchLegacyClientBikes(true)
    if (legacy !== 'unavailable') return legacy
    return unifiedClientBikesFromAssets(await listClientAssetsDetails())
  } catch {
    return listClientBikes()
  }
}

/** Single bike + telemetry. Legacy bike routes, then unified asset list match by device id. */
export async function getClientBike(imei: string): Promise<ClientBike | null> {
  const needle = String(imei).trim()
  if (!needle) return null

  if (SINGLE_SCHEMA_ENABLED) {
    const assets = await listClientAssetsDetails()
    const fromList = assets.find(
      (asset) =>
        isBikeAssetType(readClientAssetType(asset)) &&
        String(asset.go_metro_device_id ?? asset.device_id ?? '') === needle,
    )
    return fromList ? clientVehicleToClientBike(fromList) : null
  }

  const region = getRegionForApi()
  const id = encodeURIComponent(String(imei).trim())
  const headers = { ...getAuthHeaders(), 'X-Region': region }
  try {
    const res = await valtFetch(`${API_BASE}/client/bikes/${id}?region=${encodeURIComponent(region)}`, { headers })
    if (res.status === 404) {
      /* unified fallback below */
    } else if (res.ok) {
      const data = await res.json()
      const row = (data as any)?.bike ?? (data as any)?.data ?? data
      return normalizeClientBikeTelemetry(row)
    }
  } catch {
    /* fallback */
  }
  try {
    const res = await valtFetch(`${API_BASE}/bikes/${id}?region=${encodeURIComponent(region)}`, { headers })
    if (res.status === 404) {
      /* unified fallback below */
    } else if (res.ok) {
      const data = await res.json()
      const row = (data as any)?.bike ?? (data as any)?.data ?? data
      return normalizeClientBikeTelemetry(row)
    }
  } catch {
    /* unified fallback */
  }

  const details = await listClientAssetsDetails()
  const fromDetails = details.find(
    (asset) =>
      isBikeAssetType(readClientAssetType(asset)) &&
      String(asset.go_metro_device_id ?? asset.device_id ?? '') === needle,
  )
  if (fromDetails) return clientVehicleToClientBike(fromDetails)

  const list = await listClientAssets()
  const fromList = list.find(
    (asset) =>
      isBikeAssetType(readClientAssetType(asset)) &&
      String(asset.go_metro_device_id ?? asset.device_id ?? '') === needle,
  )
  return fromList ? clientVehicleToClientBike(fromList) : null
}

/** PATCH /client/bikes/{imei}/status */
export async function updateClientBikeStatus(imei: string, status: string): Promise<ClientBike> {
  const region = getRegionForApi()
  const id = encodeURIComponent(String(imei).trim())
  const res = await valtFetch(`${API_BASE}/client/bikes/${id}/status?region=${encodeURIComponent(region)}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new ClientPortalApiError(
      (data as any)?.message || (data as any)?.error || 'Failed to update bike status',
      { status: res.status, response: data },
    )
  }
  const row = (data as any)?.bike ?? (data as any)?.data ?? data
  const bike = normalizeClientBikeTelemetry(row)
  if (!bike) throw new ClientPortalApiError('Invalid bike response from server.', { status: res.status, response: data })
  return bike
}

/** PATCH /bikes/{imei} — update name, model, or status in registry. */
export async function updateClientBike(
  imei: string,
  payload: { name?: string; model?: string; status?: string },
): Promise<ClientBike> {
  const region = getRegionForApi()
  const id = encodeURIComponent(String(imei).trim())
  const res = await valtFetch(`${API_BASE}/bikes/${id}?region=${encodeURIComponent(region)}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new ClientPortalApiError(
      (data as any)?.message || (data as any)?.error || 'Failed to update bike',
      { status: res.status, response: data },
    )
  }
  const row = (data as any)?.bike ?? (data as any)?.data ?? data
  const bike = normalizeClientBikeTelemetry(row)
  if (!bike) throw new ClientPortalApiError('Invalid bike response from server.', { status: res.status, response: data })
  return bike
}

/** Unassign bike from client fleet. DELETE /client/bikes/{imei} */
export async function unassignClientBikeFromFleet(imei: string): Promise<void> {
  const region = getRegionForApi()
  const id = encodeURIComponent(String(imei).trim())
  const res = await valtFetch(`${API_BASE}/client/bikes/${id}?region=${encodeURIComponent(region)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new ClientPortalApiError(
      (data as any)?.message || (data as any)?.error || 'Failed to remove bike from fleet',
      { status: res.status, response: data },
    )
  }
}

export interface ClientBikeHistoryResult {
  bike?: ClientBike
  items: Record<string, unknown>[]
  range?: { from?: string; to?: string }
}

/** Telemetry log for one bike. Legacy bike history, then unified GET /client/vehicles/{deviceId}/history. */
export async function getBikeHistory(
  deviceId: string | number,
  params: { preset?: string; from?: string; to?: string; perPage?: number } = {},
): Promise<ClientBikeHistoryResult> {
  const preset = params.preset ?? 'today'
  const perPage = params.perPage ?? 50
  const cacheKey = `${String(deviceId)}:${preset}:${perPage}:${params.from ?? ''}:${params.to ?? ''}`
  const cached = bikeHistoryInflight.get(cacheKey)
  if (cached) return cached

  const promise = fetchBikeHistory(deviceId, params).finally(() => {
    bikeHistoryInflight.delete(cacheKey)
  })
  bikeHistoryInflight.set(cacheKey, promise)
  return promise
}

async function findClientBikeAssetByDeviceId(deviceId: string | number): Promise<ClientBike | undefined> {
  const assets = await listClientAssets()
  const bikeAsset = assets.find(
    (asset) =>
      isBikeAssetType(readClientAssetType(asset)) &&
      String(asset.go_metro_device_id ?? asset.device_id ?? '') === String(deviceId),
  )
  return bikeAsset ? clientVehicleToClientBike(bikeAsset) ?? undefined : undefined
}

async function fetchBikeHistory(
  deviceId: string | number,
  params: { preset?: string; from?: string; to?: string; perPage?: number },
): Promise<ClientBikeHistoryResult> {
  if (SINGLE_SCHEMA_ENABLED) {
    const unified = await getClientVehicleHistory({
      deviceId,
      preset: params.preset,
      from: params.from,
      to: params.to,
      perPage: params.perPage,
    })
    const bike = await findClientBikeAssetByDeviceId(deviceId)
    return { bike, items: unified.items, range: unified.range }
  }

  const region = getRegionForApi()
  const id = encodeURIComponent(String(deviceId))
  const query = new URLSearchParams({ region })
  if (params.preset) query.set('preset', params.preset)
  if (params.from) query.set('from', params.from)
  if (params.to) query.set('to', params.to)
  if (params.perPage != null) query.set('perPage', String(params.perPage))
  const headers = { ...getAuthHeaders(), 'X-Region': region }
  const qs = query.toString()

  const tryUrl = async (base: string) => {
    const res = await valtFetch(`${base}?${qs}`, { headers })
    const json = (await res.json().catch(() => ({}))) as any
    return { res, json }
  }

  let res: Response
  let json: any
  const clientAttempt = await tryUrl(`${API_BASE}/client/bikes/${id}/history`)
  if (clientAttempt.res.ok) {
    res = clientAttempt.res
    json = clientAttempt.json
  } else {
    const legacy = await tryUrl(`${API_BASE}/bikes/${id}/history`)
    if (legacy.res.ok) {
      res = legacy.res
      json = legacy.json
    } else if (clientAttempt.res.status === 404 && legacy.res.status === 404) {
      const unified = await getClientVehicleHistory({
        deviceId,
        preset: params.preset,
        from: params.from,
        to: params.to,
        perPage: params.perPage,
      })
      const bike = await findClientBikeAssetByDeviceId(deviceId)
      return {
        bike,
        items: unified.items,
        range: unified.range,
      }
    } else {
      res = legacy.res
      json = legacy.json
    }
  }

  if (!res.ok) {
    throw new Error(json?.message || json?.error || 'Failed to load bike history')
  }
  const payload = json?.data != null && typeof json.data === 'object' ? json.data : json
  const rawItems = payload?.log?.items ?? payload?.items ?? (Array.isArray(payload) ? payload : [])
  const items = Array.isArray(rawItems) ? rawItems : []
  const bikeRaw = payload?.bike
  const bike = bikeRaw ? normalizeClientBikeTelemetry(bikeRaw) : null
  const range = payload?.range as { from?: string; to?: string } | undefined
  return { bike: bike ?? undefined, items, range }
}
